import React, { useEffect, useState } from "react";
import { ric } from "@/lib/ric";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Truck, Loader2, CheckCircle, XCircle } from "lucide-react";

// PO Queue: supervisor accepts/rejects/partial-accepts (with per-line kg + reason);
// staff builds the despatch note (yarn item + bin + dye lot + kg per accepted line).
export default function RICPOQueue() {
  const { user } = useAuth();
  const { lang } = useLang();
  const isSup = user?.role === "ric_supervisor";
  const t = (en, hi) => (lang === "en" ? en : hi);

  const [pos, setPos] = useState([]);
  const [bins, setBins] = useState([]);
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState("");
  const [acceptDraft, setAcceptDraft] = useState({}); // line_id -> kg
  const [rejectReason, setRejectReason] = useState("");
  const [despatchDraft, setDespatchDraft] = useState({}); // po_line_id -> {yarn_item_id, from_bin_id, dye_lot, kg}
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = async () => setPos(await ric.listPOs());
  useEffect(() => {
    (async () => { setBins(await ric.bins()); setItems(await ric.yarnItems()); load(); })();
  }, []);

  const openPO = (p) => {
    setOpenId(p.id);
    setMsg("");
    setAcceptDraft(Object.fromEntries(p.lines.map((ln) => [ln.id, ln.kg_accepted ?? ln.kg_requested])));
    setDespatchDraft({});
    setRejectReason("");
  };

  const accept = async (p) => {
    setBusy(true);
    try {
      await ric.acceptPO(p.id, {
        lines: p.lines.map((ln) => ({ line_id: ln.id, kg_accepted: parseFloat(acceptDraft[ln.id] || 0) })),
        reject_reason: rejectReason || null,
      });
      setMsg(t("PO updated", "PO अपडेट"));
      await load(); setOpenId("");
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  const dispatch = async (p) => {
    const lines = p.lines
      .filter((ln) => despatchDraft[ln.id])
      .map((ln) => ({ po_line_id: ln.id, ...despatchDraft[ln.id], kg: parseFloat(despatchDraft[ln.id].kg || 0) }))
      .filter((d) => d.kg > 0);
    if (lines.length === 0) { setMsg(t("Enter kg for at least one line", "कम से कम एक लाइन पर कि. दर्ज करें")); return; }
    setBusy(true);
    try {
      await ric.buildDespatch(p.id, { lines });
      setMsg(t("Despatch recorded", "प्रेषण दर्ज"));
      await load(); setOpenId("");
    } catch (e) { setMsg(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  return (
    <div data-testid="ric-po-queue">
      <h1 className="text-2xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--brand-navy)", fontFamily: "'Fraunces', serif" }}>
        <Truck size={24} /> {t("MG Yarn PO queue", "MG यार्न PO लाइन")}
      </h1>
      {msg && <div className="mb-3 text-xs bg-amber-50 border border-amber-200 rounded p-2" data-testid="ric-po-msg">{msg}</div>}
      <div className="space-y-3">
        {pos.map((p) => {
          const isOpen = openId === p.id;
          const acceptable = isSup && p.status === "Sent";
          const despatchable = ["Accepted", "Partially Accepted", "Picking"].includes(p.status);
          return (
            <div key={p.id} className="hag-surface p-4" data-testid={`ric-po-${p.id}`}>
              <div className="flex justify-between flex-wrap gap-2">
                <div>
                  <div className="text-sm font-semibold" style={{ color: "var(--brand-navy)" }}>
                    {p.po_number} · {new Date(p.created_at).toLocaleDateString()}
                  </div>
                  <div className="text-xs text-[#7C8E9E]">{p.lines.length} {t("lines", "लाइनें")} · Status: <b>{p.status}</b></div>
                </div>
                <button data-testid={`ric-po-open-${p.id}`} onClick={() => openPO(p)}
                  className="h-9 px-3 text-xs rounded-md border border-[#1B3A5C]/20 hover:bg-[#FAF6F0]">
                  {isOpen ? t("Close", "बंद") : t("Open", "खोलें")}
                </button>
              </div>

              {isOpen && (
                <div className="mt-3 border-t border-[#1B3A5C]/10 pt-3 space-y-3">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-[10px] uppercase tracking-widest text-[#7C8E9E]">
                        <th className="text-left py-1">{t("Line", "लाइन")}</th>
                        <th className="text-right">Req</th>
                        <th className="text-right">Acc</th>
                        <th className="text-right">Desp</th>
                        <th className="text-right">Recv</th>
                        {acceptable && <th className="text-right">{t("Accept kg", "स्वीकृत कि.")}</th>}
                        {despatchable && <th className="text-left pl-3">{t("Despatch (item, bin, dye lot, kg)", "प्रेषण")}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {p.lines.map((ln) => (
                        <tr key={ln.id} className="border-t border-[#1B3A5C]/5" data-testid={`ric-po-line-${ln.id}`}>
                          <td className="py-2">{ln.yarn_type} · {ln.yarn_count || ""} · {ln.colour || ""}</td>
                          <td className="text-right">{ln.kg_requested}</td>
                          <td className="text-right">{ln.kg_accepted ?? "—"}</td>
                          <td className="text-right">{ln.kg_despatched || 0}</td>
                          <td className="text-right">{ln.kg_received || 0}</td>
                          {acceptable && (
                            <td className="text-right">
                              <input data-testid={`ric-po-accept-${ln.id}`} type="number" min="0" max={ln.kg_requested} step="0.1"
                                value={acceptDraft[ln.id] ?? ""}
                                onChange={(e) => setAcceptDraft({ ...acceptDraft, [ln.id]: e.target.value })}
                                className="w-20 h-8 px-1 border border-[#1B3A5C]/15 rounded text-right text-xs" />
                            </td>
                          )}
                          {despatchable && (
                            <td className="pl-3">
                              <DespatchLineInputs line={ln} bins={bins} items={items}
                                value={despatchDraft[ln.id]}
                                onChange={(v) => setDespatchDraft({ ...despatchDraft, [ln.id]: v })} />
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {acceptable && (
                    <div className="flex gap-2 flex-wrap items-end pt-2">
                      <label className="flex-1 min-w-[200px]">
                        <div className="text-[10px] uppercase tracking-widest font-bold text-[#4A627A] mb-1">{t("Reject/partial reason", "अस्वीकृति/आंशिक कारण")}</div>
                        <input data-testid={`ric-po-reason-${p.id}`} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                          className="w-full h-10 px-2 border border-[#1B3A5C]/15 rounded-md text-sm" />
                      </label>
                      <button data-testid={`ric-po-accept-btn-${p.id}`} onClick={() => accept(p)} disabled={busy}
                        className="h-10 px-4 rounded-md text-sm font-semibold text-white flex items-center gap-1"
                        style={{ background: "var(--brand-navy)" }}>
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />} {t("Save", "सहेजें")}
                      </button>
                    </div>
                  )}

                  {despatchable && (
                    <button data-testid={`ric-po-dispatch-btn-${p.id}`} onClick={() => dispatch(p)} disabled={busy}
                      className="h-10 px-4 rounded-md text-sm font-semibold text-white flex items-center gap-1"
                      style={{ background: "var(--brand-navy)" }}>
                      {busy ? <Loader2 size={14} className="animate-spin" /> : <Truck size={14} />} {t("Build despatch note", "प्रेषण नोट बनाएँ")}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
        {pos.length === 0 && <div className="text-sm text-[#7C8E9E]">{t("No POs yet.", "अभी कोई PO नहीं।")}</div>}
      </div>
    </div>
  );
}

function DespatchLineInputs({ line, bins, items, value, onChange }) {
  const v = value || { yarn_item_id: "", from_bin_id: "", dye_lot: "", kg: "" };
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-4 gap-1">
      <select data-testid={`ric-desp-item-${line.id}`} value={v.yarn_item_id} onChange={(e) => set("yarn_item_id", e.target.value)}
        className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs bg-white">
        <option value="">item</option>
        {items.filter((i) => i.yarn_type === line.yarn_type).map((i) => <option key={i.id} value={i.id}>{i.yarn_count} · {i.colour}</option>)}
      </select>
      <select data-testid={`ric-desp-bin-${line.id}`} value={v.from_bin_id} onChange={(e) => set("from_bin_id", e.target.value)}
        className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs bg-white">
        <option value="">bin</option>
        {bins.map((b) => <option key={b.id} value={b.id}>{b.bin_number}</option>)}
      </select>
      <input data-testid={`ric-desp-lot-${line.id}`} placeholder="lot" value={v.dye_lot} onChange={(e) => set("dye_lot", e.target.value)}
        className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs" />
      <input data-testid={`ric-desp-kg-${line.id}`} placeholder="kg" type="number" step="0.1" value={v.kg} onChange={(e) => set("kg", e.target.value)}
        className="h-8 px-1 border border-[#1B3A5C]/15 rounded text-xs text-right" />
    </div>
  );
}
